/**
 * Quiet background score for the start and home screens, written for Hello DinDin in the
 * spirit of slow-building film scores (warm low pad, a soft heartbeat-like pulse, a few
 * high glints) — no melody borrowed from anyone. Generated with Web Audio: no files.
 *
 * It only plays after her first tap (browser rule), ducks the moment she taps the belly so
 * the music-box notes stay clear, comes back after a few quiet seconds, and stops when the
 * app is in the background.
 */

const BPM = 66;
const BEAT = 60 / BPM;
const BAR = BEAT * 4;
// A minor → F → C → G, two bars each: a slow 16-bar arc that loops
const CHORDS: number[][] = [
  [57, 60, 64], [57, 60, 64], [53, 57, 60], [53, 57, 60],
  [48, 55, 60, 64], [48, 55, 60, 64], [55, 59, 62], [55, 59, 62]
];
const GLINT = [72, 74, 76, 79, 81, 84];   // pentatonic, for the occasional high note
const hz = (m: number) => 440 * 2 ** ((m - 69) / 12);

/** Schedule `count` bars starting at bar index `from`, at audio time `t0`, into `out`. Pure enough to render offline. */
export function scheduleBars(a: BaseAudioContext, out: AudioNode, t0: number, from: number, count: number, rand: () => number = Math.random): void {
  for (let b = 0; b < count; b++) {
    const bar = from + b;
    const t = t0 + b * BAR;
    const chord = CHORDS[Math.floor(bar / 2) % (CHORDS.length / 2) * 2]!;
    const lift = Math.min(1, bar / 8);           // the arc: everything grows over the first bars
    if (bar % 2 === 0) pad(a, out, chord, t, BAR * 2, 0.035 + 0.02 * lift);
    sub(a, out, chord[0]! - 12, t, BAR, 0.05);
    if (bar >= 2) for (let i = 0; i < 8; i++) {   // heartbeat-like eighth-note pulse, root–fifth–octave–fifth
      const m = chord[0]! + [0, 7, 12, 7][i % 4]!;
      pluck(a, out, m, t + i * BEAT / 2, 0.016 + 0.012 * lift * (i % 2 ? 0.6 : 1));
    }
    if (bar >= 4 && rand() < 0.6) glint(a, out, GLINT[Math.floor(rand() * GLINT.length)]!, t + BEAT * (1 + Math.floor(rand() * 3)), 0.03);
  }
}

function pad(a: BaseAudioContext, out: AudioNode, notes: number[], t: number, dur: number, vol: number) {
  const f = a.createBiquadFilter(); f.type = 'lowpass'; f.Q.value = 0.4;
  f.frequency.setValueAtTime(500, t); f.frequency.linearRampToValueAtTime(1300, t + dur * 0.5); f.frequency.linearRampToValueAtTime(600, t + dur);
  const g = a.createGain();
  g.gain.setValueAtTime(0.0001, t); g.gain.linearRampToValueAtTime(vol, t + dur * 0.35); g.gain.linearRampToValueAtTime(vol * 0.8, t + dur * 0.8); g.gain.linearRampToValueAtTime(0.0001, t + dur + 0.6);
  f.connect(g).connect(out);
  for (const m of notes) for (const det of [-7, 6]) {
    const o = a.createOscillator(); o.type = 'sawtooth'; o.frequency.value = hz(m - 12); o.detune.value = det;
    o.connect(f); o.start(t); o.stop(t + dur + 0.7);
  }
}

function sub(a: BaseAudioContext, out: AudioNode, m: number, t: number, dur: number, vol: number) {
  const o = a.createOscillator(); o.type = 'sine'; o.frequency.value = hz(m);
  const g = a.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.linearRampToValueAtTime(vol, t + 0.8); g.gain.linearRampToValueAtTime(0.0001, t + dur + 0.4);
  o.connect(g).connect(out); o.start(t); o.stop(t + dur + 0.5);
}

function pluck(a: BaseAudioContext, out: AudioNode, m: number, t: number, vol: number) {
  const o = a.createOscillator(); o.type = 'triangle'; o.frequency.value = hz(m);
  const f = a.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 1400;
  const g = a.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.45);
  o.connect(f).connect(g).connect(out); o.start(t); o.stop(t + 0.5);
}

function glint(a: BaseAudioContext, out: AudioNode, m: number, t: number, vol: number) {
  for (const [mult, amp, d] of [[1, 1, 2.2], [2, 0.25, 1]] as const) {
    const o = a.createOscillator(); o.type = 'sine'; o.frequency.value = hz(m) * mult;
    const g = a.createGain(); g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol * amp, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    o.connect(g).connect(out); o.start(t); o.stop(t + d + 0.05);
  }
}

/** A soft room so it feels like it is playing around her, not in her ear. */
export function ambientBus(a: BaseAudioContext, dest: AudioNode): GainNode {
  const input = a.createGain();
  const dry = a.createGain(); dry.gain.value = 0.8;
  const d1 = a.createDelay(1); d1.delayTime.value = 0.37;
  const fb = a.createGain(); fb.gain.value = 0.35;
  const tone = a.createBiquadFilter(); tone.type = 'lowpass'; tone.frequency.value = 1800;
  input.connect(dry).connect(dest);
  input.connect(d1).connect(tone).connect(fb).connect(d1);
  tone.connect(dest);
  return input;
}

// ---------------------------------------------------------------- live player
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let input: GainNode | null = null;
let timer = 0;
let nextBar = 0;
let nextTime = 0;
let wanted = false;
/** Parked (2026-11-02): needs a stronger melody before it ships. Flip to true to bring it back. */
const SCORE_ON = false;
let enabled = true;
let duckTimer = 0;
const LEVEL = 0.9;

function tick() {
  if (!ctx || !input) return;
  while (nextTime < ctx.currentTime + BAR * 1.5) {   // keep ~1.5 bars scheduled ahead
    scheduleBars(ctx, input, nextTime, nextBar, 1);
    nextBar++; nextTime += BAR;
  }
}

function startNow() {
  if (!ctx || !wanted || !enabled || ctx.state !== 'running' || timer || document.hidden) return;
  master ??= (() => { const g = ctx!.createGain(); g.gain.value = 0; g.connect(ctx!.destination); return g; })();
  input ??= ambientBus(ctx, master);
  nextBar = 0; nextTime = ctx.currentTime + 0.1;
  master.gain.cancelScheduledValues(ctx.currentTime);
  master.gain.setValueAtTime(master.gain.value, ctx.currentTime);
  master.gain.linearRampToValueAtTime(LEVEL, ctx.currentTime + 4);
  tick(); timer = window.setInterval(tick, 400);
}

function stopNow(fade = 1.2) {
  window.clearInterval(timer); timer = 0;
  if (ctx && master) { master.gain.cancelScheduledValues(ctx.currentTime); master.gain.setValueAtTime(master.gain.value, ctx.currentTime); master.gain.linearRampToValueAtTime(0, ctx.currentTime + fade); }
}

document.addEventListener('visibilitychange', () => { if (document.hidden) stopNow(0.3); else startNow(); });

export const ambient = {
  /** The sfx module hands over its AudioContext once audio is unlocked by a tap. */
  attach(a: AudioContext) { ctx = a; startNow(); },
  /** Screens that want the score (start, home) call want(true) on mount and want(false) on unmount. */
  want(on: boolean) { wanted = on && SCORE_ON; if (wanted) startNow(); else stopNow(); },
  setEnabled(on: boolean) { enabled = on; if (on) startNow(); else stopNow(0.3); },
  /** She tapped the belly: dip the music right away, bring it back after a few quiet seconds. */
  duck() {
    if (!ctx || !master || !timer) return;
    const now = ctx.currentTime;
    master.gain.cancelScheduledValues(now); master.gain.setValueAtTime(master.gain.value, now); master.gain.linearRampToValueAtTime(0.12, now + 0.25);
    window.clearTimeout(duckTimer);
    duckTimer = window.setTimeout(() => { if (ctx && master && timer) { const n = ctx.currentTime; master.gain.cancelScheduledValues(n); master.gain.setValueAtTime(master.gain.value, n); master.gain.linearRampToValueAtTime(LEVEL, n + 3); } }, 6000);
  }
};
